import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import styles from "../dealerDashBoard/DashboardStyles.module.scss";
import dashboardStyles from "../dealerDashBoard/dashboardStyles";
import { Link } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Clear from "@mui/icons-material/Clear";
import { withTranslation } from "react-i18next";
import DealerNotificationsClient from "../../../shared/clients/DealerNotificationsClient";
import UiDateUtils from "../../../utils/UiDateUtils";
import UpperCaseText from "../../../utils/UpperCaseText";
import HasPermissionTo from "../../../common/HasPermissionTo";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import crStyles from "../../customerReservation/crStyles";
import NavigateNext from "@mui/icons-material/NavigateNext";

const DealerNotifications = (props) => {
  const [notifications, setNotifications] = useState([]);
  const [renderApp, setRenderApp] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  useEffect(() => {
    props.hideOrShow(true, true);
    DealerNotificationsClient.getDealerNotifications(props.user, currentPage)
      .then((data) => {
        populateState(data);
      })
      .catch(() => {})
      .finally(() => {
        props.hideOrShow(false);
        setRenderApp(true);
      });
  }, []);

  const handlePrevious = () => {
    DealerNotificationsClient.getDealerNotifications(
      props.user,
      currentPage - 1
    )
      .then((data) => {
        populateState(data);
      })
      .catch()
      .finally(() => {
        props.hideOrShow(false);
      });
  };

  const handleNext = () => {
    DealerNotificationsClient.getDealerNotifications(
      props.user,
      currentPage + 1
    )
      .then((data) => {
        populateState(data);
      })
      .catch()
      .finally(() => {
        props.hideOrShow(false);
      });
  };

  const populateState = (data) => {
    setNotifications(data?.ordersData || []);
    setTotalCount(data?.pagination?.totalCount || 0);
    setTotalPages(data?.pagination?.totalPages || 0);
    setCurrentPage(data?.pagination?.page || 0);
    setHasNext(data?.pagination?.hasNext || false);
    setHasPrevious(data?.pagination?.hasPrevious || false);
  };

  const markNotificationRead = (id) => {
    DealerNotificationsClient.markNotificationStatusRead(props.user, id);
  };

  const deleteNotification = (id) => {
    DealerNotificationsClient.deleteNotification(props.user, id).then(() => {
      setNotifications(
        notifications.filter((notification) => notification.uid !== id)
      );
      setTotalCount((prevCount) => prevCount - 1);
    });
  };

  const isViewSDLRRestricted = (notification) => {
    return props.isMainViewingSDLR(
      props.user.dealerGroupingsMap[notification.vendorId]
    );
  };

  const getType = (notification) => {
    if (notification.type && isFinanceType(UpperCaseText(notification.type))) {
      return props.t("DealerDashboard.orderUpdate");
    } else {
      switch (notification.type) {
        case "PAID_RESERVATION":
          return UpperCaseText(props.t("CustomerReservations.created"));
        case "RESERVATION_CONFIRMED":
          return UpperCaseText(props.t("CustomerReservations.created"));
        case "DEALER_CANCELLED":
        case "CANCELLED":
          return UpperCaseText(props.t("CustomerReservations.cancelled"));
        case "RESERVATION_CANCELLED":
          return UpperCaseText(props.t("CustomerReservations.cancelled"));
        case "RESERVATION_AMENDED":
          return UpperCaseText(props.t("CustomerReservations.amended"));
        case "OFFLINE_AMMEND":
          return UpperCaseText(props.t("CustomerReservations.amended"));
        case "ORDERED":
        case "ORDER_CONTRACTED":
          return UpperCaseText(props.t("CustomerReservations.ordered"));
        case "TRADEIN_ADDED":
          return UpperCaseText(props.t("DealerDashboard.orderUpdate"));
        default:
          return UpperCaseText(notification.type);
      }
    }
  };

  const getIconType = (notification) => {
    if (notification.type && isFinanceType(UpperCaseText(notification.type))) {
      return getFinanceStatusType(UpperCaseText(notification.status)) ===
        "INFO" ? (
        <ErrorOutlineIcon
          data-testid={`infoIcon-${notification.params.reservationId}`}
          className={styles.actionIconOrders}
        />
      ) : (
        <ReportProblemOutlinedIcon
          data-testid={`problemIcon-${notification.params.reservationId}`}
          className={styles.actionIconOrders}
        />
      );
    } else {
      switch (notification.type) {
        case "FAILED_FINANCE":
          return (
            <ReportProblemOutlinedIcon
              data-testid={`problemIcon-${notification.params.reservationId}`}
              className={styles.actionIconOrders}
            />
          );
        default:
          return (
            <ErrorOutlineIcon
              data-testid={`infoIcon-${notification.params.reservationId}`}
              className={styles.actionIconOrders}
            />
          );
      }
    }
  };

  const getSpec = (notification) => {
    if (notification.type === "TRADEIN_ADDED") {
      return props.t("DealerDashboard.tradeinDescription");
    } else if (notification.specification) {
      return ` - ${notification.specification}`;
    } else if (notification.params.NotificationMsg) {
      return notification.params.NotificationMsg.includes(
        notification.params.reservationId
      )
        ? ` ${notification.params.NotificationMsg.replace(
            notification.params.reservationId,
            ""
          )}`
        : ` - ${notification.params.NotificationMsg}`;
    } else {
      return;
    }
  };

  const getFinanceStatusType = (status) => {
    switch (status) {
      case "SUBMITTED":
      case "APPROVED":
      case "CONDITIONED":
      case "CANCELLED":
      default:
        return "INFO";
      case "OPT_OFFLINE":
      case "ERROR":
      case "DENIED":
      case "FINANCE_EID_CHECKED_FAIL":
      case "FINANCE_REFERRED":
        return "ACTION";
    }
  };

  const isFinanceType = (type) => {
    return type === "FINANCE_STATUS" || type === "FINANCE_FAILED";
  };

  const isLeaseType = (type) => {
    return type === "LEASE_STATUS" || type === "LEASE_FAILED";
  };

  const getLinkIconType = (notification) => {
    if (
      (notification.type && isFinanceType(UpperCaseText(notification.type))) ||
      isLeaseType(UpperCaseText(notification.type))
    ) {
      return getFinanceStatusType(UpperCaseText(notification.status)) === "INFO"
        ? props.t("DealerDashboard.view")
        : props.t("DealerDashboard.gotoPage");
    } else {
      return props.t("DealerDashboard.view");
    }
  };

  const getFinanceDesc = (notification) => {
    switch (UpperCaseText(notification.status)) {
      case "APPROVED":
        return props.t("DealerDashboard.approvedDescription");
      case "SUBMITTED":
        return props.t("DealerDashboard.submittedDescription");
      case "CONDITIONED":
        return props.t("DealerDashboard.conditionedDescription");
      case "ERROR":
      case "FINANCE_EID_CHECKED_FAIL":
      case "FINANCE_REFERRED":
        return props.t("DealerDashboard.errorDescription");
      case "DENIED":
        return props.t("DealerDashboard.deniedDescription");
      case "CANCELLED":
        return props.t("DealerDashboard.cancelledDescription");
      case "OPT_OFFLINE":
        return props.t("DealerDashboard.optofflineDescription");
      default:
        return ` - ${notification.status}`;
    }
  };

  const getLeaseDesc = (notification) => {
    switch (UpperCaseText(notification.status)) {
      case "APPROVED":
        return props.t("DealerDashboard.approvedDescription");
      case "SUBMITTED":
        return props.t("DealerDashboard.leaseSubmittedDescription");
      case "CONDITIONED":
        return props.t("DealerDashboard.leaseConditionedDescription");
      case "ERROR":
        return props.t("DealerDashboard.errorDescription");
      case "DENIED":
        return props.t("DealerDashboard.leaseDeniedDescription");
      case "CANCELLED":
        return props.t("DealerDashboard.leaseCancelledDescription");
      case "OPT_OFFLINE":
        return props.t("DealerDashboard.optofflineLeaseDescription");
      default:
        return ` - ${notification.status}`;
    }
  };

  const getTableHeader = (style, divStyle, column) => {
    return (
      <TableCell id={column + column} style={style}>
        <div className={divStyle}>{props.t("OrdersDashboard." + column)}</div>
      </TableCell>
    );
  };

  const onPage = notifications.length > 0 ? currentPage + 1 : 0;

  let orders = [];
  if (notifications.length > 0) {
    orders = notifications;
  }

  return (
    renderApp && (
      <div>
        <div
          style={dashboardStyles.header}
          className={styles.notificationHeader}
        >
          {props.t("DealerDashboard.ordersHeader")} (
          <span data-testid="allNotificationCount">{totalCount}</span>)
        </div>
        <Table data-testid="notificationsTable" key="dealerDashBoardTable">
          <TableHead>
            <TableRow
              style={dashboardStyles.dashboardHeader}
              className={styles.tableHeaderLine}
            >
              {getTableHeader(
                dashboardStyles.typeHeader,
                styles.alignStart,
                "type"
              )}
              {getTableHeader(
                dashboardStyles.orderIdHeader,
                styles.alignStart,
                "description"
              )}
              {getTableHeader(
                dashboardStyles.dateHeader,
                styles.alignStart,
                "pendingDueDate"
              )}
              <TableCell style={dashboardStyles.actionHeader}>
                <div className={styles.alignStart}>
                  {" "}
                  {props.t("OrdersDashboard.action")}{" "}
                </div>
              </TableCell>
              <TableCell style={dashboardStyles.emptyHeader} />
            </TableRow>
          </TableHead>

          <TableBody>
            {orders.map((notification, index) => (
              <TableRow
                className={
                  notification.siteMessageStatus === "READ"
                    ? styles.textContentBodyListRead
                    : styles.textContentBodyListNew
                }
                id={"row" + index}
                key={index}
              >
                <TableCell style={dashboardStyles.leftCell}>
                  {getIconType(notification)}
                  {(notification.value = getType(notification))}
                </TableCell>
                <TableCell style={dashboardStyles.centerCell}>
                  {notification.params.reservationId}
                  {isFinanceType(notification.type)
                    ? getFinanceDesc(notification)
                    : isLeaseType(notification.type)
                    ? getLeaseDesc(notification)
                    : getSpec(notification)}
                </TableCell>
                <TableCell
                  className={styles.col3}
                  style={dashboardStyles.centerCell}
                >
                  {notification.sentDate
                    ? UiDateUtils.formattedDate(notification.sentDate)
                    : props.t("DealerDashboard.noDeadline")}
                  {getFinanceStatusType(UpperCaseText(notification.status)) ===
                  "INFO"
                    ? ""
                    : props.t("DealerDashboard.mediumPriority")}
                </TableCell>

                <TableCell
                  style={dashboardStyles.centerCell}
                  id="reservation"
                  className={styles.col4}
                >
                  <Link
                    id="reservationLink"
                    onClick={() => {
                      markNotificationRead(notification.uid);
                    }}
                    to={`/dealer/${notification.vendorId}/customer-reservations/order-details/${notification.params.reservationId}`}
                    className={styles.tableLink}
                  >
                    {getLinkIconType(notification)}
                    <ChevronRightIcon style={dashboardStyles.viewIcon} />
                  </Link>
                </TableCell>

                <HasPermissionTo
                  permissions={props.user.permissions.rolePermissions}
                  perform={["deleteNotification"]}
                  condition={!isViewSDLRRestricted(notification)}
                  render={() => {
                    return (
                      <TableCell
                        data-testid={`deleteNotification${index}`}
                        style={dashboardStyles.rightCell}
                        className={styles.cursorButton}
                        onClick={() => deleteNotification(notification.uid)}
                      >
                        <div className={styles.closeButton}>
                          <Clear />
                        </div>
                      </TableCell>
                    );
                  }}
                  noRender={() => {
                    return <TableCell>&nbsp;</TableCell>;
                  }}
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className={styles.pagesDiv}>
          <button
            disabled={hasPrevious === false}
            data-testid="prev"
            onClick={() => handlePrevious()}
            className={styles.buttonStyle}
          >
            <NavigateBefore
              style={
                hasPrevious === false
                  ? crStyles.arrowDisabled
                  : crStyles.arrowEnabled
              }
            />
          </button>
          <div className={styles.pages}>
            {onPage} / {totalPages}
          </div>
          <button
            disabled={hasNext === false}
            data-testid="next"
            onClick={() => handleNext()}
            className={styles.buttonStyle}
          >
            <NavigateNext
              style={
                hasNext === false
                  ? crStyles.arrowDisabled
                  : crStyles.arrowEnabled
              }
            />
          </button>
        </div>
      </div>
    )
  );
};

export default withTranslation("emp")(DealerNotifications);
